import React, {Dispatch, SetStateAction} from 'react'
import {Button, Icon, Number, Select, Text} from '@therms/atalaya'
import {
  LabeledInput,
  RecordTagTitles,
  RPCDocs,
  SelectRecordTag,
  toOptions,
  useRecordsFieldOptionsConfig,
  useUrlState,
} from '@therms/react-modules'
import {
  RecordSearchState,
  VehicleSearch,
} from '@src/modules/Records/shared/RecordsList'
import {IoClose} from 'react-icons/io5'
import {MdExpandLess, MdExpandMore} from 'react-icons/md'
import {TbArrowBigRightFilled} from 'react-icons/tb'

interface RecordVehicleSearchFiltersProps {
  filtersState: VehicleSearch | undefined
  searchText?: string
  setFiltersState: Dispatch<SetStateAction<RecordSearchState>>
  setSearchText: Dispatch<SetStateAction<string>>
  setYear: Dispatch<SetStateAction<undefined | {from?: number; to?: number}>>
  year?: {from?: number; to?: number}
}

function RecordVehicleSearchFilters({
  filtersState,
  searchText,
  setFiltersState,
  setSearchText,
  setYear,
  year,
}: RecordVehicleSearchFiltersProps) {
  const [showAdvanceFilters, setAdvanceFilters] = useUrlState(
    'recordvehiclefilters',
    false,
  )
  const config = useRecordsFieldOptionsConfig()

  return (
    <div className="flex w-full flex-col">
      <div className="pb-xxs gap-x-xxs inline-flex">
        <Text
          placeholder="Search..."
          onChangeValue={setSearchText}
          value={searchText}
          search
        />

        <Button
          onClick={() => setAdvanceFilters(!showAdvanceFilters)}
          size="base"
          subtle
          variant="neutral"
        >
          <span>Filters</span>
          {showAdvanceFilters ? (
            <Icon Icon={MdExpandLess} />
          ) : (
            <Icon Icon={MdExpandMore} />
          )}
        </Button>

        {(filtersState?.values?.color ||
          filtersState?.values?.type ||
          filtersState?.values?.year?.to ||
          filtersState?.values?.year?.from ||
          !!filtersState?.values?.tags?.length ||
          !!searchText) && (
          <Button
            onClick={() => {
              setAdvanceFilters(false)
              setFiltersState({
                vehicle: {
                  values: {color: undefined, tags: [], type: undefined},
                },
              })
              setYear({from: undefined, to: undefined})
              setSearchText('')
            }}
            subtle
          >
            <Icon color="critical" Icon={IoClose} /> Clear
          </Button>
        )}
      </div>

      {showAdvanceFilters && (
        <div className="gap-xxs xxl:grid-cols-7 grid grid-cols-2 md:grid-cols-5">
          <div className="col-span-2">
            <LabeledInput
              label="Type"
              nameElement={<div>{filtersState?.values?.type}</div>}
              inputElement={
                <Select
                  allowCreate
                  isClearable
                  menuIsOpen
                  options={toOptions(config?.vehicle.type)}
                  onChangeValue={(val) => {
                    setFiltersState({vehicle: {values: {type: val}}})
                  }}
                  placeholder="Type"
                  value={filtersState?.values?.type}
                />
              }
              value={filtersState?.values?.type}
            />
          </div>

          <div className="col-span-2">
            <LabeledInput
              label="Colors"
              nameElement={<div>{filtersState?.values?.color}</div>}
              inputElement={
                <Select
                  allowCreate
                  isClearable
                  menuIsOpen
                  options={toOptions(config?.vehicle.color)}
                  onChangeValue={(val) => {
                    setFiltersState({vehicle: {values: {color: val}}})
                  }}
                  placeholder="Colors"
                  value={filtersState?.values?.color}
                />
              }
              value={filtersState?.values?.color}
            />
          </div>

          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <LabeledInput
              label="Record Tags"
              nameElement={
                <RecordTagTitles
                  listAll={false}
                  recordTagIds={filtersState?.values?.tags}
                />
              }
              inputElement={
                <SelectRecordTag
                  typeFilter={[
                    RPCDocs.Records.GetRecordTagsList.v1.TypeFilter.Vehicle,
                  ]}
                  isClearable
                  combineSelected
                  menuIsOpen
                  multi
                  onChangeValue={(val) =>
                    setFiltersState({
                      vehicle: {values: {tags: val}},
                    })
                  }
                  placeholder="Tags"
                  value={filtersState?.values?.tags}
                />
              }
              value={filtersState?.values?.tags}
            />
          </div>

          <div className="col-span-2 md:col-span-4 lg:col-span-3">
            <LabeledInput
              label="Year"
              nameElement={
                <div className="text-xs">
                  {!!filtersState?.values?.year?.from &&
                    !filtersState?.values?.year?.to && (
                      <div>From {filtersState?.values?.year?.from}</div>
                    )}

                  {!!filtersState?.values?.year?.to &&
                    !filtersState?.values?.year?.from && (
                      <div>Up To {filtersState?.values?.year?.to}</div>
                    )}

                  {!!filtersState?.values?.year?.from &&
                    !!filtersState?.values?.year?.to && (
                      <div>
                        From {filtersState?.values?.year?.from} To{' '}
                        {filtersState?.values?.year?.to}
                      </div>
                    )}
                </div>
              }
              onCancel={() => setYear({from: undefined, to: undefined})}
              inputElement={
                <div className="gap-x-xxs bg-input-background inline-flex w-full items-center justify-center">
                  <Number
                    onChangeInteger={(val) => {
                      setYear({from: val, to: year?.to})
                    }}
                    placeholder="0000"
                    value={year?.from}
                  />

                  {/* <span>To</span> */}
                  <div className="px-xxs my-auto">
                    <Icon color="subtle" Icon={TbArrowBigRightFilled} />
                  </div>

                  <Number
                    onChangeInteger={(val) => {
                      setYear({from: year?.from, to: val})
                    }}
                    placeholder="0000"
                    value={year?.to}
                  />
                </div>
              }
              value={
                !!filtersState?.values?.year?.from ||
                !!filtersState?.values?.year?.to
                  ? [
                      `${filtersState?.values?.year?.from}`,
                      `${filtersState?.values?.year?.to}`,
                    ]
                  : []
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export {RecordVehicleSearchFilters}